import { supabase, run } from './db';

/**
 * Estoque de congelados: o que está dentro do botijão agora.
 *
 * "Agora" é `destination = 'frozen'`, e só. Transferido saiu, fresco nunca
 * entrou, descartado não existe mais — a view já filtra. Estoque é o que dá
 * para ir buscar.
 *
 * Nada aqui é enfileirável. Organizar botijão é trabalho de bancada, com
 * sinal; e localização é o tipo de dado que duas pessoas mexem ao mesmo tempo
 * enquanto arrumam o mesmo botijão — enfileirar faria a última sincronização
 * ganhar em silêncio.
 */

export const listarCongelados = (laboratoryId) =>
  run(supabase
    .from('vw_estoque_congelado')
    .select('*')
    .eq('laboratory_id', laboratoryId)
    .order('client_name')
    .order('numero'));

/**
 * TUDO O QUE É DE UM CLIENTE, numa chamada.
 *
 * Quatro consultas em paralelo porque são quatro perguntas independentes, e
 * esperar uma para fazer a outra só somaria a latência de todas. O que chega é
 * cru: quem dá forma é `extratoDeEstoque`, que é puro e testado.
 *
 * O MOVIMENTO DE SÊMEN É FILTRADO PELAS PARTIDAS DO CLIENTE, e não por um
 * `client_id` próprio: `vw_movimentos_semen` não tem essa coluna, porque o dono
 * mora na partida e não no lançamento. Sem partida não há movimento a buscar, e
 * a quinta consulta não sai.
 */
export const estoqueDoCliente = async (laboratoryId, clientId) => {
  if (!laboratoryId || !clientId) {
    return { congelados: [], saidas: [], partidas: [], movimentos: [] };
  }

  const [congelados, saidas, partidas] = await Promise.all([
    run(supabase.from('vw_estoque_congelado').select('*')
      .eq('laboratory_id', laboratoryId).eq('client_id', clientId)
      .order('doadora').order('touro')),

    run(supabase.from('vw_saidas_de_embrioes').select('*')
      .eq('laboratory_id', laboratoryId).eq('client_id', clientId)
      .order('saida_em', { ascending: false })),

    run(supabase.from('vw_saldo_semen').select('*')
      .eq('laboratory_id', laboratoryId).eq('client_id', clientId)
      .order('touro').order('codigo')),
  ]);

  const ids = (partidas || []).map(p => p.semen_batch_id).filter(Boolean);
  const movimentos = ids.length === 0 ? [] : await run(supabase
    .from('vw_movimentos_semen').select('*')
    .in('semen_batch_id', ids)
    .order('data', { ascending: false }));

  return {
    congelados: congelados || [],
    saidas: saidas || [],
    partidas: partidas || [],
    movimentos: movimentos || [],
  };
};

/**
 * Carimba botijão, caneca e data de congelamento em vários embriões.
 *
 * Em lote porque é assim que o trabalho acontece: ninguém guarda uma palheta
 * por vez, guarda a caneca inteira de um cliente. Um por um transformaria
 * arrumar 15 embriões em 15 confirmações.
 *
 * Campo vazio vira NULL em vez de string vazia — `''` é um local que parece
 * preenchido e não leva a lugar nenhum.
 */
export const definirLocal = (embryoIds, { botijao, caneca, rack, altura, freeze_date }) =>
  run(supabase.from('embryos').update({
    botijao:     botijao?.trim() || null,
    caneca:      caneca?.trim() || null,
    rack:        rack?.trim() || null,
    // '' viraria violação do check; nulo é o "não informado" que ele aceita.
    altura:      altura || null,
    // Só mexe na data quando foi informada: apagar a data de congelamento de
    // quem já tem, só porque o campo veio vazio ao mover de caneca, seria
    // perder dado numa ação que não é sobre data.
    ...(freeze_date ? { freeze_date } : {}),
  }).in('id', embryoIds));

// ── Regras puras, testáveis ───────────────────────────────────────────────

const chave = (l) => [l.botijao, l.caneca, l.rack, l.altura]
  .map(v => v || '').join(' ');

/**
 * Agrupa por botijão e caneca, para responder "o que tem no Botijão A".
 *
 * Quem não tem local sai num grupo próprio e PRIMEIRO. É o grupo que precisa
 * de ação: embrião congelado sem localização é embrião que ninguém acha.
 */
export const agruparPorLocal = (linhas = []) => {
  const mapa = new Map();
  for (const l of linhas) {
    const k = chave(l);
    if (!mapa.has(k)) {
      mapa.set(k, {
        botijao: l.botijao || null, caneca: l.caneca || null,
        rack: l.rack || null, altura: l.altura || null, itens: [],
      });
    }
    mapa.get(k).itens.push(l);
  }
  return [...mapa.values()].sort((a, b) => {
    const aSem = !a.botijao, bSem = !b.botijao;
    if (aSem !== bSem) return aSem ? -1 : 1;
    return (a.botijao || '').localeCompare(b.botijao || '')
        || (a.caneca  || '').localeCompare(b.caneca  || '')
        || (a.rack    || '').localeCompare(b.rack    || '')
        // cima antes de baixo: é a ordem em que a mão entra na caneca.
        || (a.altura === b.altura ? 0 : a.altura === 'cima' ? -1 : 1);
  });
};

/** Agrupa por cliente — é a pergunta "o que eu tenho guardado de fulano". */
export const agruparPorCliente = (linhas = []) => {
  const mapa = new Map();
  for (const l of linhas) {
    const k = l.client_id || l.client_name || 'sem-cliente';
    if (!mapa.has(k)) {
      mapa.set(k, { client_id: l.client_id || null, client_name: l.client_name || 'Sem cliente', itens: [] });
    }
    mapa.get(k).itens.push(l);
  }
  return [...mapa.values()].sort((a, b) => a.client_name.localeCompare(b.client_name));
};

/**
 * Os números que a tela mostra no topo.
 *
 * `semLocal` existe como número próprio porque é a única métrica aqui que pede
 * ação. Diluída no total, ninguém repara que 25 de 25 estão perdidos.
 */
export const resumoDoEstoque = (linhas = []) => {
  const semLocal = linhas.filter(l => !l.botijao).length;
  const botijoes = new Set(linhas.filter(l => l.botijao).map(l => l.botijao));
  const clientes = new Set(linhas.map(l => l.client_id || l.client_name).filter(Boolean));
  return {
    total: linhas.length,
    semLocal,
    botijoes: botijoes.size,
    clientes: clientes.size,
    vitrificados: linhas.filter(l => l.metodo === 'Vitrificação').length,
    dt:           linhas.filter(l => l.metodo === 'Direct Transfer').length,
  };
};

/** "7, 8, 9, 10" vira "7-10"; a etiqueta da caneca cabe na tela. */
export const faixaDeNumeros = (numeros = []) => {
  const ns = [...new Set(numeros.filter(n => Number.isFinite(n)))].sort((a, b) => a - b);
  if (ns.length === 0) return '';
  const partes = [];
  let ini = ns[0], fim = ns[0];
  for (let i = 1; i <= ns.length; i++) {
    if (ns[i] === fim + 1) { fim = ns[i]; continue; }
    partes.push(ini === fim ? `${ini}` : fim === ini + 1 ? `${ini}, ${fim}` : `${ini}-${fim}`);
    ini = ns[i]; fim = ns[i];
  }
  return partes.join(', ');
};

/**
 * O endereço, escrito do jeito que se procura: botijão, caneca, rack, altura.
 *
 * Para de escrever no primeiro campo vazio em vez de imprimir "· —": endereço
 * pela metade é o que existe hoje, e emendar traços faz parecer defeito.
 */
export const enderecoDoBotijao = ({ botijao, caneca, rack, altura } = {}) => {
  if (!botijao) return '';
  const partes = [botijao];
  if (caneca) partes.push(`caneca ${caneca}`);
  if (rack)   partes.push(`rack ${rack}`);
  if (altura) partes.push(altura);
  return partes.join(' · ');
};

/**
 * Entrada de embrião que chegou de fora.
 *
 * Embrião comprado, ou recebido de outro laboratório: existe no botijão sem
 * ter saído de uma FIV daqui.
 *
 * PERTENCE A UM CLIENTE, NÃO A UM SERVIÇO.
 *   Serviço é o trabalho que ESTE laboratório executou. Embrião que veio
 *   pronto não saiu de trabalho nenhum daqui, e obrigar a escolher um serviço
 *   fazia inventar vínculo — ou pior, pendurar o embrião no serviço errado só
 *   para conseguir salvar.
 *
 *   O que importa é de quem ele é, porque é isso que diz a quem devolver a
 *   palheta. `embryos.client_id` responde direto; um CHECK no banco garante
 *   que todo embrião tenha serviço OU cliente — sem os dois, ele existe no
 *   botijão sem dono.
 *
 * O NÚMERO É OPCIONAL E VEM DA ETIQUETA.
 *   `service_number` é o número dentro de um serviço, e não há serviço aqui.
 *   O número que vale é o que quem produziu escreveu na palheta — se veio,
 *   digita-se; se não veio, fica nulo em vez de o sistema inventar um.
 */
export const darEntradaDeEmbrioes = async ({
  laboratoryId, clientId, origem, quantidade,
  doadora, touro, stage, metodo, freeze_date, numeroInicial,
  botijao, caneca, rack, altura, observacao,
}) => {
  const qtd = parseInt(quantidade) || 0;
  if (!laboratoryId || !clientId || !origem || qtd < 1) {
    throw new Error('Informe o cliente, quem produziu e uma quantidade maior que zero.');
  }

  const inicio = parseInt(numeroInicial) || null;

  const linhas = Array.from({ length: qtd }, (_, i) => ({
    id:               crypto.randomUUID(),
    laboratory_id:    laboratoryId,
    client_id:        clientId,
    origem,
    service_id:       null,
    // Numera em sequência a partir do informado; sem número, todos ficam
    // nulos. Meia numeração seria pior que nenhuma.
    service_number:   inicio === null ? null : inicio + i,
    donor_identifier: doadora?.trim() || null,
    bull_name:        touro?.trim() || null,
    stage:            stage || null,
    destination:      'frozen',
    freeze_method:    metodo || null,
    freeze_date:      freeze_date || null,
    botijao:          botijao?.trim() || null,
    caneca:           caneca?.trim() || null,
    rack:             rack?.trim() || null,
    altura:           altura || null,
    freeze_location:  observacao?.trim() || null,
  }));

  await run(supabase.from('embryos').insert(linhas));
  return { quantidade: qtd, inicio, fim: inicio === null ? null : inicio + qtd - 1 };
};

// ── Saída do botijão que não é transferência ──────────────────────────────

/**
 * Motivos pelos quais um embrião deixa o botijão sem ir para uma receptora.
 *
 * Lista fechada pelo mesmo motivo de sempre: livre, "venda" e "vendido" e
 * "VENDA" viram três motivos no relatório.
 *
 * `descarte` aqui NÃO é o mesmo que `destination = 'discard'` da FIV. Lá o
 * embrião foi descartado na classificação e nunca chegou ao botijão; aqui ele
 * estava guardado e foi jogado fora. Momentos diferentes, e o relatório de
 * saídas só deve mostrar o segundo.
 */
export const MOTIVOS_DE_SAIDA = [
  { id: 'venda',     label: 'Venda',                 pedeValor: true },
  { id: 'devolucao', label: 'Devolução ao cliente' },
  { id: 'envio',     label: 'Envio a outro laboratório' },
  { id: 'descarte',  label: 'Descarte' },
  { id: 'perda',     label: 'Perda' },
  { id: 'outro',     label: 'Outro' },
];

export const MOTIVO_POR_ID = Object.fromEntries(MOTIVOS_DE_SAIDA.map(m => [m.id, m]));

export const rotuloDoMotivo = (id) => (id ? (MOTIVO_POR_ID[id]?.label || id) : '');

/**
 * Tira embriões do botijão.
 *
 * Em lote, como o resto da tela: sai a caneca de um cliente, não uma palheta.
 *
 * O valor é gravado POR EMBRIÃO, e quem chama recebe o total já dividido — a
 * venda é negociada pelo lote ("R$ 7.000 pelos dois"), mas guardar só o total
 * na primeira linha faria o relatório por embrião mentir.
 */
export const darSaida = (embryoIds, {
  motivo, saida_em, destino, valorTotal, retirado_por, observacao,
}) => {
  if (!embryoIds?.length) throw new Error('Nenhum embrião selecionado.');
  if (!motivo || !saida_em) throw new Error('Informe o motivo e a data da saída.');

  const total = Number(valorTotal) || 0;
  const porEmbriao = total > 0
    ? Math.round((total / embryoIds.length) * 100) / 100
    : null;

  return run(supabase.from('embryos').update({
    destination:      'saida',
    saida_em,
    saida_motivo:     motivo,
    saida_destino:    destino?.trim() || null,
    saida_valor:      porEmbriao,
    saida_por:        retirado_por?.trim() || null,
    saida_observacao: observacao?.trim() || null,
  }).in('id', embryoIds));
};

export const listarSaidas = (laboratoryId, { de, ate } = {}) => {
  let q = supabase
    .from('vw_saidas_de_embrioes')
    .select('*')
    .eq('laboratory_id', laboratoryId)
    .order('saida_em', { ascending: false });
  if (de)  q = q.gte('saida_em', de);
  if (ate) q = q.lte('saida_em', ate);
  return run(q);
};

/** Os números do relatório de saídas. */
export const resumoDeSaidas = (linhas = []) => {
  const soma = (f) => linhas.reduce((s, l) => s + (f(l) || 0), 0);
  const porMotivo = new Map();
  for (const l of linhas) {
    const k = l.motivo || 'outro';
    const a = porMotivo.get(k) || { motivo: k, embrioes: 0, valor: 0 };
    a.embrioes += 1;
    a.valor += Number(l.valor) || 0;
    porMotivo.set(k, a);
  }
  return {
    embrioes: linhas.length,
    // Só a venda tem valor; somar tudo daria a impressão de que descarte rende.
    valor: soma(l => (l.motivo === 'venda' ? Number(l.valor) : 0)),
    vendidos: linhas.filter(l => l.motivo === 'venda').length,
    porMotivo: [...porMotivo.values()].sort((a, b) => b.embrioes - a.embrioes),
    clientes: new Set(linhas.map(l => l.client_id || l.client_name).filter(Boolean)).size,
  };
};

// ── Haste de vitrificação ─────────────────────────────────────────────────

/**
 * Junta embriões vitrificados numa haste — nova, sem número, ou a informada.
 *
 * Por RPC porque as regras moram no banco: só vitrificado no botijão, uma
 * haste é de um cliente só, e a haste fica num lugar só. Devolve o número.
 */
export const juntarEmHaste = (embryoIds, haste = null) =>
  run(supabase.rpc('junta_em_haste', {
    p_embryo_ids: embryoIds,
    p_haste: haste ? parseInt(haste) : null,
  }));

export const separarDaHaste = (embryoIds) =>
  run(supabase.rpc('junta_em_haste', { p_embryo_ids: embryoIds, p_separar: true }));

/**
 * A seleção sempre leva a haste inteira.
 *
 * A haste não se abre pela metade: dar saída ou mudar de caneca um embrião só
 * deixaria o sistema dizendo que os outros ficaram onde não estão.
 */
export const comAHasteInteira = (ids, linhas = []) => {
  const hastes = new Set(linhas.filter(l => ids.has(l.embryo_id) && l.haste).map(l => l.haste));
  if (hastes.size === 0) return ids;
  const n = new Set(ids);
  linhas.forEach(l => { if (l.haste && hastes.has(l.haste)) n.add(l.embryo_id); });
  return n;
};

/**
 * "Quantas hastes de quantos embriões" — a pergunta de quem vai descongelar.
 *
 * O cliente pede três; o que se responde é se existe haste com três. Embrião
 * vitrificado sem haste entra à parte, porque não se sabe com quem está.
 */
export const hastesPorTamanho = (linhas = []) => {
  const porHaste = new Map();
  let soltos = 0;
  for (const l of linhas) {
    if (l.metodo !== 'Vitrificação') continue;
    if (!l.haste) { soltos += 1; continue; }
    porHaste.set(l.haste, (porHaste.get(l.haste) || 0) + 1);
  }
  const porTamanho = new Map();
  for (const qtd of porHaste.values()) porTamanho.set(qtd, (porTamanho.get(qtd) || 0) + 1);
  return {
    tamanhos: [...porTamanho.entries()]
      .map(([embrioes, hastes]) => ({ embrioes, hastes }))
      .sort((a, b) => a.embrioes - b.embrioes),
    soltos,
  };
};

/** "1 emb.: 2 hastes · 3 emb.: 1 haste · 4 sem haste" */
export const textoDasHastes = ({ tamanhos = [], soltos = 0 } = {}) => {
  const partes = tamanhos.map(t =>
    `${t.hastes} haste${t.hastes > 1 ? 's' : ''} de ${t.embrioes}`);
  if (soltos > 0) partes.push(`${soltos} sem haste`);
  return partes.join(' · ');
};

/** Dentro do grupo, quem divide haste fica junto, e a haste na ordem do número. */
export const ordenaPorHaste = (itens = []) =>
  itens.map((e, i) => ({ e, i }))
    .sort((a, b) => (a.e.haste ?? Infinity) - (b.e.haste ?? Infinity) || a.i - b.i)
    .map(({ e }) => e);
